import {
  ChevronDownIcon,
  ListBulletIcon,
  Squares2X2Icon
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import Tooltip from 'components/layout/tooltip';
import { SystemOptionType } from 'data/types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { PICK_DEFINITIONS, PickKind } from './equipment-comparison-utils';
import SwapCardWrapper from './swap-card-wrapper';
import { SwapListModel } from './swap-list-model';
import TopPickCard from './top-pick-card';

type SystemListPanelProps = Readonly<{
  model: SwapListModel;
  selectedId: string | null;
  disabledIds: ReadonlySet<string>;
  onSelect: (system: SystemOptionType) => void;
}>;

const PAGE_SIZE = 10;

export default function SystemListPanel({
  model,
  selectedId,
  disabledIds,
  onSelect
}: SystemListPanelProps) {
  const {
    picks,
    customSystems,
    moreSystems,
    rebateSystemIds,
    showMore,
    onToggleShowMore,
    viewMode,
    onViewModeChange,
    serverHasMore,
    isLoadingMore,
    onLoadMore,
    shownCount,
    totalCount
  } = model;

  const pickEntries = useMemo(
    () =>
      PICK_DEFINITIONS.map(({ kind }) => ({
        kind,
        system: picks[kind]
      })).filter(
        (entry): entry is { kind: PickKind; system: SystemOptionType } =>
          entry.system !== null
      ),
    [picks]
  );

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    if (!showMore) setVisibleCount(PAGE_SIZE);
  }, [showMore]);

  const previousMoreCountRef = useRef(moreSystems.length);
  useEffect(() => {
    const previousLength = previousMoreCountRef.current;
    previousMoreCountRef.current = moreSystems.length;
    setVisibleCount((current) => {
      if (moreSystems.length === 0) return PAGE_SIZE;
      // When a server page appends items while the user is parked at the
      // bottom (everything previously loaded already revealed), reveal ONE
      // chunk of the newly arrived items. The sentinel can't do it: it never
      // left the viewport, so no intersection transition fires. Revealing a
      // chunk pushes the sentinel down so the rest of the page reveals
      // through normal scroll-driven sentinel chunks — which the arming gate
      // in the observer callback keeps trustworthy (one fetch per genuine
      // bottom arrival).
      if (
        moreSystems.length > previousLength &&
        previousLength > 0 &&
        current >= previousLength
      ) {
        return Math.min(current + PAGE_SIZE, moreSystems.length);
      }
      return Math.min(Math.max(current, PAGE_SIZE), moreSystems.length);
    });
  }, [moreSystems.length]);

  useEffect(() => {
    if (!showMore || selectedId === null) return;
    const selectedIndex = moreSystems.findIndex((s) => s.id === selectedId);
    if (selectedIndex === -1) return;
    setVisibleCount((current) => Math.max(current, selectedIndex + 1));
  }, [showMore, selectedId, moreSystems]);

  const visibleMore = showMore ? moreSystems.slice(0, visibleCount) : [];
  const hasMoreToReveal = showMore && visibleCount < moreSystems.length;
  // Chain client-side reveal into server-side load-more: only fetch the next
  // page once every already-loaded combination is visible.
  const showSentinel = showMore && (hasMoreToReveal || serverHasMore);

  const lastScrolledIdRef = useRef<string | null>(null);
  const handleSelectedCardRef = (node: HTMLDivElement | null) => {
    if (!node || selectedId === null) return;
    if (lastScrolledIdRef.current === selectedId) return;
    lastScrolledIdRef.current = selectedId;
    node.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  // Edge-triggered arming gate for server fetches. A freshly created
  // IntersectionObserver always fires an initial notification reporting
  // current state (isIntersecting: true when the sentinel is in view) — not
  // a transition. Because reveals change this effect's deps and recreate the
  // observer, a level-triggered check would treat each initial notification
  // as a new bottom arrival and loop fetches. The ref survives observer
  // recreations: a fetch disarms it, and only a genuine exit
  // (isIntersecting: false) re-arms it.
  const loadMoreArmedRef = useRef(true);
  useEffect(() => {
    if (!showSentinel) return undefined;
    const node = sentinelRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          loadMoreArmedRef.current = true;
          return;
        }
        if (hasMoreToReveal) {
          setVisibleCount((current) =>
            Math.min(current + PAGE_SIZE, moreSystems.length)
          );
        } else if (
          loadMoreArmedRef.current &&
          serverHasMore &&
          !isLoadingMore
        ) {
          loadMoreArmedRef.current = false;
          onLoadMore();
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: '200px 0px',
        threshold: 0
      }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [
    showSentinel,
    hasMoreToReveal,
    moreSystems.length,
    serverHasMore,
    isLoadingMore,
    onLoadMore
  ]);

  return (
    <div className='flex max-h-[70vh] w-[360px] flex-col'>
      <div className='shrink-0 border-b border-gray-200 bg-white px-3 pb-2 pt-3'>
        <div className='flex items-center justify-between'>
          <h2 className='text-lg font-bold text-gray-900'>Systems</h2>
          <span
            role='group'
            aria-label='System list view mode'
            className='isolate inline-flex rounded-md shadow-sm'
          >
            <button
              type='button'
              aria-pressed={viewMode === 'compact'}
              aria-label='Compact view'
              onClick={() => onViewModeChange('compact')}
              className={clsx(
                'relative inline-flex cursor-pointer items-center justify-center gap-1 rounded-l-md px-2 py-1 ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500',
                viewMode === 'compact'
                  ? 'bg-blue-200'
                  : 'bg-white hover:bg-blue-100'
              )}
            >
              <ListBulletIcon className='h-4 w-4' />
              <Tooltip text='Compact view' />
            </button>
            <button
              type='button'
              aria-pressed={viewMode === 'detailed'}
              aria-label='Detailed view'
              onClick={() => onViewModeChange('detailed')}
              className={clsx(
                'relative -ml-px inline-flex cursor-pointer items-center justify-center gap-1 rounded-r-md px-2 py-1 ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500',
                viewMode === 'detailed'
                  ? 'bg-blue-200'
                  : 'bg-white hover:bg-blue-100'
              )}
            >
              <Squares2X2Icon className='h-4 w-4' />
              <Tooltip text='Detailed view' />
            </button>
          </span>
        </div>
        <p className='mt-1 text-xs text-gray-500'>
          Select a system to swap into this column.
        </p>
        {totalCount > 0 && (
          <p className='mt-0.5 text-xs text-gray-500'>
            Showing {Math.min(shownCount, totalCount)} of {totalCount} system
            {totalCount === 1 ? '' : 's'}
          </p>
        )}
      </div>
      <div
        ref={scrollContainerRef}
        className='flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3'
      >
        {customSystems.length > 0 && (
          <div className='flex flex-col gap-2'>
            <div className='text-xs font-semibold uppercase tracking-wider text-gray-500'>
              Custom Systems
            </div>
            {customSystems.map((system) => (
              <SwapCardWrapper
                key={system.id}
                disabled={disabledIds.has(system.id)}
                cardRef={
                  system.id === selectedId ? handleSelectedCardRef : undefined
                }
              >
                <TopPickCard
                  system={system}
                  kind={null}
                  isSelected={system.id === selectedId}
                  onSelect={onSelect}
                  hasRebates={rebateSystemIds.has(system.id)}
                  compact
                  viewMode={viewMode}
                />
              </SwapCardWrapper>
            ))}
          </div>
        )}
        <div className='flex flex-col gap-3'>
          {customSystems.length > 0 && (
            <div className='text-xs font-semibold uppercase tracking-wider text-gray-500'>
              Ranked Systems
            </div>
          )}
          {pickEntries.map(({ kind, system }) => (
            <SwapCardWrapper
              key={system.id}
              disabled={disabledIds.has(system.id)}
              cardRef={
                system.id === selectedId ? handleSelectedCardRef : undefined
              }
            >
              <TopPickCard
                system={system}
                kind={kind}
                isSelected={system.id === selectedId}
                showTopMatchBadge={kind === 'bestOverall'}
                onSelect={onSelect}
                hasRebates={rebateSystemIds.has(system.id)}
                viewMode={viewMode}
              />
            </SwapCardWrapper>
          ))}
        </div>
        {moreSystems.length > 0 && !showMore && (
          <button
            type='button'
            className='inline-flex items-center justify-center gap-1 self-center px-2 py-2 text-sm font-medium text-gray-600 hover:text-gray-900'
            onClick={onToggleShowMore}
          >
            Show more systems
            <ChevronDownIcon className='h-4 w-4' />
          </button>
        )}
        {showMore && visibleMore.length > 0 && (
          <div className='flex flex-col gap-2'>
            {visibleMore.map((system, index) => (
              <SwapCardWrapper
                key={system.id}
                disabled={disabledIds.has(system.id)}
                cardRef={
                  system.id === selectedId ? handleSelectedCardRef : undefined
                }
              >
                <TopPickCard
                  system={system}
                  kind={null}
                  isSelected={system.id === selectedId}
                  onSelect={onSelect}
                  hasRebates={rebateSystemIds.has(system.id)}
                  compact
                  systemNumber={
                    picks.list.length + customSystems.length + index + 1
                  }
                  viewMode={viewMode}
                />
              </SwapCardWrapper>
            ))}
            {showSentinel && (
              <div
                ref={sentinelRef}
                // overflow-anchor:none stops the browser from pinning the
                // viewport to the sentinel when new cards insert above it, so
                // the sentinel can exit the viewport naturally and re-arm the
                // load-more gate.
                className='py-2 text-center text-xs text-gray-400 [overflow-anchor:none]'
              >
                {isLoadingMore ? 'Loading more...' : '\u00A0'}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
